import pako from "pako";
import { useState, useEffect, useRef } from "react";
import { Link, useParams } from "wouter";
import SearchHeader from "@/components/search-header";
import ArtworkDisplay from "@/components/artwork-display";
import KnowledgeGraph from "@/components/knowledge-graph";
import EmotionBarChart from "@/components/emotion-bar-chart";
import Footer from "@/components/footer";
import SEOHead from "@/components/SEOHead";
import { artworkSeoDescription, artworkSeoTitle } from "@shared/seo";

import {
  loadAllArtworks,
  loadArtworkById,
  getRandomArtwork,
  searchArtworks,
  StaticArtwork,
  loadEmotionData,
  EmotionData,
} from "@/lib/static-data";
import { trackEvent } from "@/lib/analytics";
import NotFound from "@/pages/not-found";

export default function Home() {
  const siteUrl = "https://ariadnesgallery.com";
  const params = useParams();
  const [currentArtworkId, setCurrentArtworkId] = useState<number | null>(null);
  const [currentArtwork, setCurrentArtwork] = useState<any>(null);
  const [isLoadingArtwork, setIsLoadingArtwork] = useState(false);
  // True when the URL names an artwork that doesn't exist, so the page can say
  // so instead of showing the loading spinner forever.
  const [artworkNotFound, setArtworkNotFound] = useState(false);
  const [connectionDepth, setConnectionDepth] = useState<number>(1);
  const [searchResults, setSearchResults] = useState<StaticArtwork[]>([]);
  const [comprehensiveArtworks, setComprehensiveArtworks] = useState<
    StaticArtwork[]
  >([]);
  const [localArtworks, setLocalArtworks] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentEmotionData, setCurrentEmotionData] = useState<any[]>([]);
  const [hoveredEmotionData, setHoveredEmotionData] = useState<any[] | null>(
    null,
  );
  const [hoveredArtworkTitle, setHoveredArtworkTitle] = useState<string>("");
  const [connectedArtworks, setConnectedArtworks] = useState<StaticArtwork[]>([]);
  const [emotionDataMap, setEmotionDataMap] = useState<Map<number, EmotionData[]>>(new Map());
  const [hoveredArtworkId, setHoveredArtworkId] = useState<number | null>(null);

  // Track the latest requested artwork ID to prevent race conditions
  const latestRequestedIdRef = useRef<number | null>(null);

  // Centralized artwork loading function that always uses full data
  const loadArtworkWithFullData = async (artworkId: number) => {
    if (!artworkId) return null;
    
    // Track this request
    latestRequestedIdRef.current = artworkId;
    const requestId = artworkId;
    
    setIsLoadingArtwork(true);
    setArtworkNotFound(false);
    try {
      // Always load full artwork data to ensure descriptions are included
      const fullArtworkData = await loadArtworkById(artworkId);
      
      // Only update state if this is still the latest request
      if (latestRequestedIdRef.current !== requestId) {
        console.log(`Ignoring stale response for artwork ${artworkId} (latest: ${latestRequestedIdRef.current})`);
        return null;
      }
      
      if (fullArtworkData) {
        console.log(`Loaded full artwork data for ${artworkId}:`, {
          id: fullArtworkData.id,
          title: fullArtworkData.title,
          hasDescription: !!fullArtworkData.description
        });
        setCurrentArtwork(fullArtworkData);
        setCurrentArtworkId(artworkId);
        return fullArtworkData;
      } else {
        console.warn(`No full data found for artwork ${artworkId}`);
        setArtworkNotFound(true);
        setCurrentArtwork(null);
        setCurrentArtworkId(null);
        return null;
      }
    } catch (error) {
      console.error(`Error loading artwork ${artworkId}:`, error);
      
      // Only clear state if this is still the latest request
      if (latestRequestedIdRef.current === requestId) {
        setArtworkNotFound(true);
        setCurrentArtwork(null);
        setCurrentArtworkId(null);
      }
      return null;
    } finally {
      // Only clear loading if this is still the latest request
      if (latestRequestedIdRef.current === requestId) {
        setIsLoadingArtwork(false);
      }
    }
  };

  // Load emotion data map
  useEffect(() => {
    const loadEmotionMap = async () => {
      try {
        const emotionMap = await loadEmotionData();
        setEmotionDataMap(emotionMap);
      } catch (error) {
        console.error("Error loading emotion data map:", error);
      }
    };

    loadEmotionMap();
  }, []);

  // Load emotion data for current artwork
  useEffect(() => {
    const loadCurrentEmotionData = () => {
      if (currentArtwork?.id && emotionDataMap.size > 0) {
        try {
          const emotions = emotionDataMap.get(currentArtwork.id);
          setCurrentEmotionData(emotions || []);
        } catch (error) {
          console.error(
            "Error loading emotion data for current artwork:",
            error,
          );
          setCurrentEmotionData([]);
        }
      }
    };

    loadCurrentEmotionData();
  }, [currentArtwork?.id, emotionDataMap]);

  // Load connected artworks for the Stendhal Score section
  useEffect(() => {
    const loadConnectedArtworks = () => {
      if (currentArtwork?.graph && Array.isArray(currentArtwork.graph) && comprehensiveArtworks.length > 0) {
        try {
          // Get up to 10 connected artworks
          const connectedIds = currentArtwork.graph.slice(0, 10);
          
          // Load the connected artworks data from comprehensive database
          const connectedArtworksData = [];
          for (const connectedId of connectedIds) {
            const connectedArtwork = comprehensiveArtworks.find(artwork => artwork.id === connectedId);
            if (connectedArtwork) {
              connectedArtworksData.push({
                ...connectedArtwork,
                imageUrl: connectedArtwork.url,
              });
            }
          }
          
          setConnectedArtworks(connectedArtworksData);
        } catch (error) {
          console.error("Error loading connected artworks:", error);
          setConnectedArtworks([]);
        }
      } else {
        setConnectedArtworks([]);
      }
    };

    loadConnectedArtworks();
  }, [currentArtwork?.graph, comprehensiveArtworks]);

  // Load static data on component mount
  useEffect(() => {
    async function loadInitialData() {
      try {
        setIsLoading(true);

        const [artworks] = await Promise.all([
          loadAllArtworks(),
        ]);

        setComprehensiveArtworks(artworks);
        setLocalArtworks(artworks);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    loadInitialData();
  }, []);

  // Update current artwork from URL params
  useEffect(() => {
    const handleUrlChange = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const artworkParam = urlParams.get("artwork");

      if (artworkParam) {
        // Handle external artwork from search results
        try {
          const artwork = JSON.parse(decodeURIComponent(artworkParam));
          if (artwork.id) {
            await loadArtworkWithFullData(artwork.id);
          }
        } catch (e) {
          console.error("Failed to parse artwork parameter");
        }
      } else if (params.id) {
        const id = parseInt(params.id);
        if (!isNaN(id) && /^\d+$/.test(params.id)) {
          // Load full artwork data from compressed json.gz file (with caching)
          await loadArtworkWithFullData(id);
        } else {
          setArtworkNotFound(true);
        }
      }
    };

    // Initial load
    handleUrlChange();

    // Listen for popstate events (browser back/forward)
    window.addEventListener("popstate", handleUrlChange);

    return () => {
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, [params.id, comprehensiveArtworks]);

  // Use the current artwork from state (loaded via centralized function)
  // currentArtwork is now managed by loadArtworkWithFullData function

  // Handle search
  const handleSearch = (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const results = searchArtworks(comprehensiveArtworks, query, 20);
    setSearchResults(results);

    // Track search event
    trackEvent("search", "artwork_search", query, results.length);
  };

  // Handle artwork selection from search
  const handleArtworkSelect = (artworkId: number) => {
    // For static deployment, navigate to the actual static page
    window.history.pushState({}, "", `/artwork/${artworkId}/`);
    // Trigger a popstate event to update the page
    window.dispatchEvent(new PopStateEvent("popstate"));

    // Track artwork view from search
    trackEvent("view_artwork", "navigation", "search_result", artworkId);
  };

  // Handle knowledge graph node clicks
  const handleGraphNodeClick = (nodeId: string) => {
    const artworkId = parseInt(nodeId.replace("artwork_", ""));
    if (isNaN(artworkId)) return;
    // For static deployment, navigate to the actual static page (consistent with search)
    window.history.pushState({}, "", `/artwork/${artworkId}/`);
    // Trigger a popstate event to update the page
    window.dispatchEvent(new PopStateEvent("popstate"));

    // Track artwork view from knowledge graph
    trackEvent("view_artwork", "navigation", "knowledge_graph", artworkId);
  };

  // Handle connected artwork hover
  const handleConnectedArtworkHover = (artwork: StaticArtwork) => {
    if (artwork.id && emotionDataMap.has(artwork.id)) {
      const emotionData = emotionDataMap.get(artwork.id);
      setHoveredEmotionData(emotionData || null);
      setHoveredArtworkTitle(artwork.title || "");
      setHoveredArtworkId(artwork.id);
    }
  };

  // Handle connected artwork hover end
  const handleConnectedArtworkHoverEnd = () => {
    setHoveredEmotionData(null);
    setHoveredArtworkTitle("");
    setHoveredArtworkId(null);
  };

  // Handle random artwork selection
  const handleRandomArtwork = () => {
    if (comprehensiveArtworks.length > 0) {
      // Only select from artworks that have static pages (first 1000)
      const randomArtwork = getRandomArtwork(comprehensiveArtworks);
      if (randomArtwork && randomArtwork.id) {
        // For static deployment, navigate to the actual static page (consistent with search)
        window.history.pushState({}, "", `/artwork/${randomArtwork.id}/`);
        // Trigger a popstate event to update the page
        window.dispatchEvent(new PopStateEvent("popstate"));

        // Track random artwork view
        trackEvent(
          "view_artwork",
          "navigation",
          "random_button",
          randomArtwork.id,
        );
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SearchHeader
          artworks={[]}
          onArtworkSelect={handleArtworkSelect}
          onSearch={handleSearch}
          totalCount={0}
          onRandomArtwork={handleRandomArtwork}
          currentArtworkId={currentArtworkId || currentArtwork?.id}
          currentArtwork={currentArtwork}
        />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Generate SEO data for current artwork
  const seoTitle = currentArtwork
    ? artworkSeoTitle(currentArtwork)
    : "Ariadne's Gallery - Explore Art Through Interactive Knowledge Graphs";

  const seoDescription = currentArtwork
    ? artworkSeoDescription(currentArtwork)
    : "Explore famous artworks and their connections through an interactive knowledge graph. Discover masterpieces, artists, and art movements.";

  const structuredData = currentArtwork
    ? {
        "@context": "https://schema.org",
        "@type": "VisualArtwork",
        name: currentArtwork.title,
        creator: {
          "@type": "Person",
          name: currentArtwork.artist,
        },
        ...(currentArtwork.year && { dateCreated: currentArtwork.year }),
        ...(currentArtwork.description && {
          description: currentArtwork.description,
        }),
        ...(currentArtwork.imageUrl && { image: currentArtwork.imageUrl }),
        ...(currentArtwork.movement && { artMedium: currentArtwork.movement }),
        url: `${siteUrl}/artwork/${currentArtwork.id}/`,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${siteUrl}/artwork/${currentArtwork.id}/`,
        },
      }
    : {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Ariadne's Gallery",
        description:
          "Explore famous artworks and their connections through an interactive knowledge graph",
        url: `${siteUrl}/`,
        potentialAction: {
          "@type": "SearchAction",
          target: `${siteUrl}/?search={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        image={currentArtwork?.imageUrl}
        url={
          currentArtwork
            ? `${siteUrl}/artwork/${currentArtwork.id}/`
            : `${siteUrl}/`
        }
        type={currentArtwork ? "article" : "website"}
        article={
          currentArtwork
            ? {
                author: currentArtwork.artist,
                publishedTime: currentArtwork.year
                  ? `${currentArtwork.year}-01-01`
                  : undefined,
              }
            : undefined
        }
        structuredData={structuredData}
      />
      <SearchHeader
        artworks={searchResults}
        onArtworkSelect={handleArtworkSelect}
        onSearch={handleSearch}
        totalCount={comprehensiveArtworks.length}
        onArtworkDisplay={(artwork) => {
          if (artwork.id) {
            loadArtworkWithFullData(artwork.id);
          }
        }}
        onRandomArtwork={handleRandomArtwork}
        currentArtworkId={currentArtworkId || currentArtwork?.id}
        currentArtwork={currentArtwork}
      />

      <main className="pt-16">
        {isLoadingArtwork ? (
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : currentArtwork ? (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-6 mb-6">
              <ArtworkDisplay artwork={currentArtwork} showTitle={true} />

              <h3 className="text-lg font-semibold mb-4">Stendhal Score</h3>

              <div className="prose prose-sm max-w-none">
                <div className="text-foreground/80 leading-relaxed" />
                This graph maps how artworks are connected through the emotions
                they awaken.
              </div>

              {/* Connected Artworks Grid */}
              {connectedArtworks.length > 0 && (
                <div className="mb-8">
                  <h4 className="text-md font-semibold mb-4">Connected Artworks</h4>
                  <div className="flex gap-2 w-full overflow-x-auto connected-artworks-scroll pb-2">
                    {connectedArtworks.map((artwork, index) => (
                      <Link
                        key={artwork.id || index}
                        href={`/artwork/${artwork.id}/`}
                        className={`group block transition-all duration-200 w-24 flex-shrink-0 ${
                          hoveredArtworkId === artwork.id 
                            ? 'ring-2 ring-[#e11d48] ring-offset-2 scale-105 shadow-lg' 
                            : 'hover:scale-105 hover:shadow-md'
                        }`}
                        onMouseEnter={() => handleConnectedArtworkHover(artwork)}
                        onMouseLeave={handleConnectedArtworkHoverEnd}
                      >
                        <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                          <img
                            src={artwork.url || "/api/placeholder/300/300"}
                            alt={artwork.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            loading="lazy"
                          />
                        </div>
                        <div className="mt-2">
                          <h5 className="text-sm font-medium truncate">
                            {artwork.title}
                          </h5>
                          <p className="text-xs text-gray-500 truncate">
                            {artwork.artist}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="flex flex-col">
                  <KnowledgeGraph
                    artworkId={currentArtworkId || currentArtwork?.id || 1}
                    onNodeClick={handleGraphNodeClick}
                    onNodeHover={(emotionData, artworkTitle, artworkId) => {
                      setHoveredEmotionData(emotionData);
                      setHoveredArtworkTitle(artworkTitle || "");
                      // Set the hovered artwork ID for highlighting
                      setHoveredArtworkId(artworkId || null);
                    }}
                  />
                </div>

                <div className="flex flex-col">
                  {currentEmotionData.length > 0 && (
                    <div className="space-y-4">
                      <EmotionBarChart
                        data={currentEmotionData}
                        hoveredData={hoveredEmotionData}
                        currentArtworkTitle={currentArtwork?.title}
                        hoveredArtworkTitle={hoveredArtworkTitle}
                        title={
                          hoveredEmotionData
                            ? "Emotion Comparison"
                            : "Current Artwork Emotions"
                        }
                        width={600}
                        height={400}
                      />
                      <div className="text-center">
                        <Link
                          href={currentArtworkId || currentArtwork?.id ? `/emotion-search/${currentArtworkId || currentArtwork?.id}/` : "/emotion-search/"}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors text-sm font-medium"
                        >
                          Find Similar Emotions
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : artworkNotFound ? (
          <NotFound />
        ) : (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-4">
                Welcome to Art Explorer
              </h2>
              <p className="text-muted-foreground">Loading artwork...</p>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mt-4"></div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
